/**
 * Phone
 * 
 * Displays a phone overlay with three states:
 * - hidden: not visible
 * - idle: bottom-left corner, only top portion visible
 * - active: centered on screen, fully visible with dimmed backdrop
 * 
 * Accepts children to render content overlaid on the phone screen area.
 * The screen area position is configurable via screenPosition prop.
 */

import { Children, ReactNode, isValidElement } from 'react';
import styles from './phone.module.css';

function hasRenderableChildren(children: ReactNode): boolean {
  return Children.toArray(children).some(
    (child) => isValidElement(child) || (typeof child === 'string' && child.trim().length > 0)
  );
}

export type PhoneState = 'hidden' | 'idle' | 'active';

export interface PhoneScreenPosition {
  top: number;    // % from top of phone image
  left: number;   // % from left of phone image
  width: number;  // % of phone image width
  height: number; // % of phone image height
}

const DEFAULT_SCREEN_POSITION: PhoneScreenPosition = {
  top: 10,
  left: 9,
  width: 83,
  height: 86,
};

interface PhoneProps {
  state: PhoneState;
  imageSrc: string;
  children?: ReactNode;
  screenPosition?: PhoneScreenPosition;
  /** Let choice-panel clicks pass through the phone backdrop. */
  withChoiceLayout?: boolean;
}

export const Phone = ({ state, imageSrc, children, screenPosition, withChoiceLayout = false }: PhoneProps) => {
  if (state === 'hidden') return null;

  const stateClass = state === 'active' ? styles.active : styles.idle;
  const pos = screenPosition || DEFAULT_SCREEN_POSITION;
  const showScreen = state === 'active' && hasRenderableChildren(children);

  return (
    <>
      {/* Dimmed backdrop for active state */}
      <div
        className={`${styles.backdrop} ${showScreen ? styles.backdropVisible : ''} ${withChoiceLayout ? styles.backdropWithChoice : ''}`}
      />

      {/* Phone */}
      <div className={`${styles.phoneWrapper} ${stateClass}`}>
        <img
          src={imageSrc}
          alt="Téléphone"
          className={styles.phoneImage}
        />
        {/* Screen overlay: positioned to match the phone screen area */}
        {showScreen && (
          <div
            className={`${styles.phoneScreen} cn-zoom-ui`}
            style={{
              top: `${pos.top}%`,
              left: `${pos.left}%`,
              width: `${pos.width}%`,
              height: `${pos.height}%`,
            }}
          >
            <div className={styles.phoneScreenContent}>
              {children}
            </div>
            <div className={styles.phoneNavBar}>
              <div className={styles.phoneNavItem}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>
                <span>Appel</span>
              </div>
              <div className={styles.phoneNavItem}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
                <span>Message</span>
              </div>
              <div className={styles.phoneNavItem}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
                <span>Contacts</span>
              </div>
              <div className={styles.phoneNavItem}>
                <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/></svg>
                <span>Alertes</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
